/**
 * A Level 1 card: a name, a line, at most two dials, and a way in.
 *
 * ============================== THE WHOLE ARGUMENT =============================
 * `PRODUCT-FLOW-AND-PRICING.md` §1 diagnoses the product as "the engine's parameter
 * list as the navigation" — eleven decisions before a hand. This component is the
 * answer to that, and it is deliberately small: if a card ever needs a third dial, a
 * second paragraph or a nested control, the thing it is describing belongs on Custom
 * Table instead. The component being cramped is the feature.
 *
 * Three states, and only three:
 *   - ordinary: dials, then Play.
 *   - VERIFYING: the card is drawn, says what is being checked, and does nothing. The
 *     Bounty Hunter card, until a bounty chart passes the gate. A drawn-and-disabled
 *     card is the honest shape — hiding it would say the product does not do knockout,
 *     and enabling it would say a model has been verified that has not.
 *   - a LINK card (Custom Table, Build a Drill): no dials, one action, smaller.
 * ===============================================================================
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { DIFFICULTY_WORDS, type Dial } from "./poker-cards";

export interface PresetCardProps {
  title: string;
  line: string;
  dials?: Dial[];
  values?: { difficulty?: number; seats?: number; depth?: number };
  onDial?: (kind: Dial["kind"], value: number) => void;
  /** The button. Either a route to push, or a handler. */
  to?: string;
  onPlay?: () => void;
  action?: string;
  /** Tournament cards. See the note on the badge below. */
  turbo?: boolean;
  /** A two-word chip beside the title: the scan-line version of `guardrail`. */
  chip?: string;
  /**
   * What a seat costs, in Bucks, and whether this player can afford it.
   *
   * A card that names a price and stops there is a card that lets somebody press Play
   * and meet a 402. When `cost.afford` is false the button goes and the card says what
   * the seat costs and what you have — and, below the floor, the Barkeep is offered
   * instead. Never a smaller seat chosen on the player's behalf.
   */
  cost?: { buyin: number; label: string; afford: boolean; short?: ReactNode };
  /** Set, the card explains itself and does nothing. */
  verifying?: string;
  /** A sentence printed on the card, quoted from the code it came from. */
  guardrail?: string;
  /** The big one on the tab: wider, and first. */
  hero?: boolean;
  /** The last, smaller card: Custom Table, Build a Drill. */
  quiet?: boolean;
  /** A card the player built and kept. */
  mine?: boolean;
  onForget?: () => void;
  children?: ReactNode;
}

export function PresetCard(props: PresetCardProps) {
  const {
    title, line, dials = [], values = {}, onDial, to, onPlay, action = "Play",
    turbo, chip, cost, verifying, guardrail, hero, quiet, mine, onForget, children,
  } = props;

  const className = [
    "pcard",
    hero ? "pcard-hero" : "",
    quiet ? "pcard-quiet" : "",
    verifying ? "pcard-verifying" : "",
  ].filter(Boolean).join(" ");

  return (
    <section className={className}>
      <div className="pcard-head">
        <h3>{title}</h3>
        {mine && <span className="pcard-mine" title="A table you saved">yours</span>}
        {chip && <span className="pcard-chip">{chip}</span>}
        {false && turbo && (
          /*
           * THE TURBO BADGE, AND WHAT IT SAYS TODAY.
           *
           * The master plan is explicit that turbo is a badge on a tournament card and
           * not a card of its own, and this is that badge. It is not a working toggle
           * yet and it does not pretend to be: turbo means faster blind levels and a
           * 15-second clock, the clock lives in the Director, and the Director is not
           * opened this phase. A badge that set a flag nothing reads would be the
           * exact dishonesty the "verifying" state exists to avoid, so it says what is
           * true — the card takes a turbo, the product cannot deal one yet.
           *
           * PHASE 4: and it is HIDDEN until it works. Three "coming" badges on one tab
           * is noise, and a badge is a much weaker promise than the verifying card's
           * whole panel — it reads as a feature you have not found rather than as one
           * that does not exist yet. The `turbo` flag stays on the cards, because the
           * cards really do take a turbo; this is the render, and it comes back on the
           * day the Director grows a 15-second clock.
           */
          <span className="pcard-badge" title="Faster levels and a 15-second clock. Not dealt yet.">
            Turbo · coming
          </span>
        )}
      </div>
      <p className="pcard-line">{line}</p>

      {verifying ? (
        <p className="pcard-verify" role="status">{verifying}</p>
      ) : (
        <>
          {dials.length > 0 && (
            <div className="pcard-dials">
              {dials.map((d) => (
                <DialControl key={d.kind} dial={d} values={values} onDial={onDial} />
              ))}
            </div>
          )}
          {children}
          {cost && (
            <p className={"pcard-cost" + (cost.afford ? "" : " pcard-cost-short")}>
              {cost.short ?? cost.label}
            </p>
          )}
          <div className="pcard-go">
            {cost && !cost.afford ? (
              /* No Play. The card has already said what the seat costs and what you
                 have; the way out of that is the Barkeep, in the header, not a
                 quietly cheaper table. */
              <span className="pcard-cant">Not enough Bucks</span>
            ) : to ? (
              <Link className={quiet ? "btn-quiet pcard-btn" : "btn pcard-btn"} to={to}>{action}</Link>
            ) : (
              <button className={quiet ? "btn-quiet pcard-btn" : "btn pcard-btn"}
                      type="button" onClick={onPlay}>{action}</button>
            )}
            {mine && onForget && (
              <button className="pcard-forget" type="button" onClick={onForget}>Forget</button>
            )}
          </div>
        </>
      )}

      {guardrail && <p className="pcard-guard">{guardrail}</p>}
    </section>
  );
}

/**
 * One dial.
 *
 * Difficulty is a slider because it is a scale and the words under it are the point;
 * seats and depth are segmented buttons because they are two or four discrete values
 * and a slider that snaps to four stops is a worse button. Both are labelled by what
 * they SAY, not by their number: "Tough", not "0.7".
 */
function DialControl({ dial, values, onDial }: {
  dial: Dial;
  values: { difficulty?: number; seats?: number; depth?: number };
  onDial?: (kind: Dial["kind"], value: number) => void;
}) {
  if (dial.kind === "difficulty") {
    const v = values.difficulty ?? dial.default;
    return (
      <label className="pcard-dial">
        <span className="pcard-dial-name">Opponents</span>
        <input type="range" min={1} max={5} step={1} value={v}
               aria-label="Opponents"
               onChange={(e) => onDial?.("difficulty", Number(e.target.value))} />
        <output className="pcard-dial-val">{DIFFICULTY_WORDS[v - 1]}</output>
      </label>
    );
  }
  const v = (dial.kind === "seats" ? values.seats : values.depth) ?? dial.default;
  const name = dial.kind === "seats" ? "Seats" : "Stack";
  return (
    <div className="pcard-dial">
      <span className="pcard-dial-name">{name}</span>
      <div className="pcard-seg" role="group" aria-label={name}>
        {dial.options.map((o) => (
          <button key={o} type="button" aria-pressed={v === o}
                  className={v === o ? "on" : ""}
                  onClick={() => onDial?.(dial.kind, o)}>
            {dial.kind === "seats" ? o : `${o}bb`}
          </button>
        ))}
      </div>
    </div>
  );
}
